import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import FanoutAnswerPanel from "./FanoutAnswerPanel";
import FanoutGraphView from "./FanoutGraphView";
import FanoutProviderFilter from "./FanoutProviderFilter";
import FanoutSourcesView from "./FanoutSourcesView";
import FanoutTimelineView from "./FanoutTimelineView";
import { FANOUT_VIEWS, parseFanoutView } from "./fanoutUtils";

const VIEW_LABELS = {
	graph: "Graph",
	timeline: "Timeline",
	sources: "Sources",
};

export default function FanoutQuerySection({
	run,
	analyses,
	activeAnalysis,
	activeProvider,
	onSelectProvider,
}) {
	const [searchParams, setSearchParams] = useSearchParams();
	const fanoutViewParam = searchParams.get("fanoutView");
	const [fanoutView, setFanoutViewState] = useState(() => parseFanoutView(fanoutViewParam));
	const [graphFullscreen, setGraphFullscreen] = useState(false);

	useEffect(() => {
		setFanoutViewState(parseFanoutView(fanoutViewParam));
	}, [fanoutViewParam]);

	useEffect(() => {
		if (fanoutView !== "graph") setGraphFullscreen(false);
	}, [fanoutView]);

	const setFanoutView = useCallback(
		(view) => {
			const nextView = parseFanoutView(view);
			setFanoutViewState(nextView);
			const next = new URLSearchParams(searchParams);
			if (nextView === "graph") {
				next.delete("fanoutView");
			} else {
				next.set("fanoutView", nextView);
			}
			setSearchParams(next, { replace: true });
		},
		[searchParams, setSearchParams]
	);

	const filteredRun = activeAnalysis
		? { ...run, analyses: [activeAnalysis] }
		: run;

	const providerFilter = (
		<FanoutProviderFilter
			analyses={analyses}
			activeAnalysis={activeAnalysis}
			onSelect={onSelectProvider}
		/>
	);

	return (
		<div className="monitoring__analysis-fanout">
			<div className="monitoring__fanout-toolbar">
				<FanoutProviderFilter
					analyses={analyses}
					activeAnalysis={activeAnalysis}
					onSelect={onSelectProvider}
					className="monitoring__fanout-toolbar-providers"
				/>
				<div
					className="monitoring__segmented monitoring__segmented--sm monitoring__fanout-view-switch"
					role="group"
					aria-label="Fan-out display"
				>
					{FANOUT_VIEWS.map((view) => (
						<button
							key={view}
							type="button"
							className={`monitoring__segmented-btn${
								fanoutView === view ? " is-active" : ""
							}`}
							onClick={() => setFanoutView(view)}
							aria-pressed={fanoutView === view}
						>
							{VIEW_LABELS[view]}
						</button>
					))}
				</div>
			</div>

			{activeAnalysis ? (
				<div className="monitoring__fanout-primary-view">
					{fanoutView === "graph" ? (
						<FanoutGraphView
							key={`graph-${run.id}-${activeProvider}`}
							run={filteredRun}
							fullscreen={graphFullscreen}
							onFullscreenChange={setGraphFullscreen}
							headerExtra={providerFilter}
						/>
					) : null}
					{fanoutView === "timeline" ? (
						<FanoutTimelineView analysis={activeAnalysis} />
					) : null}
					{fanoutView === "sources" ? (
						<FanoutSourcesView analysis={activeAnalysis} />
					) : null}
				</div>
			) : (
				<p className="text-muted small mb-0">No provider data available.</p>
			)}

			{activeAnalysis ? (
				<div className="monitoring__fanout-answer-wrap">
					<FanoutAnswerPanel analysis={activeAnalysis} />
				</div>
			) : null}
		</div>
	);
}
